// ════════════════════════════════════════════════════════════
// CMS Block Types — shared between editor, renderer, and templates
// ════════════════════════════════════════════════════════════

export type BlockType =
  | 'heading'
  | 'paragraph'
  | 'list'
  | 'callout'
  | 'divider'
  | 'quote'
  | 'spacer'
  | 'image'
  | 'table';

export interface HeadingBlock {
  type: 'heading';
  id: string;
  level: 2 | 3 | 4;
  text: string;
}

export interface ParagraphBlock {
  type: 'paragraph';
  id: string;
  text: string;
}

export interface ListBlock {
  type: 'list';
  id: string;
  style: 'bullet' | 'numbered';
  items: string[];
}

export interface CalloutBlock {
  type: 'callout';
  id: string;
  variant: 'info' | 'warning' | 'success' | 'error';
  text: string;
}

export interface DividerBlock {
  type: 'divider';
  id: string;
}

export interface QuoteBlock {
  type: 'quote';
  id: string;
  text: string;
  author?: string;
}

export interface SpacerBlock {
  type: 'spacer';
  id: string;
  size: 'sm' | 'md' | 'lg';
}

export interface ImageBlock {
  type: 'image';
  id: string;
  url: string;
  alt: string;
  caption?: string;
}

export interface TableBlock {
  type: 'table';
  id: string;
  headers: string[];
  rows: string[][];
}

export type ContentBlock =
  | HeadingBlock
  | ParagraphBlock
  | ListBlock
  | CalloutBlock
  | DividerBlock
  | QuoteBlock
  | SpacerBlock
  | ImageBlock
  | TableBlock;

// ── CMS Page (full record) ──

export interface CmsPage {
  id: string;
  slug: string;
  status: 'draft' | 'published' | 'archived';
  template: string;
  title_en: string;
  subtitle_en: string;
  content_en: ContentBlock[];
  meta_description_en: string;
  title_fr: string;
  subtitle_fr: string;
  content_fr: ContentBlock[];
  meta_description_fr: string;
  hero_gradient_from: string;
  hero_gradient_to: string;
  show_hero: boolean;
  show_header: boolean;
  show_footer: boolean;
  hero_meta_en: string;
  hero_meta_fr: string;
  is_system: boolean;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
}

// ── CMS Page (list view — lighter) ──

export interface CmsPageSummary {
  id: string;
  slug: string;
  status: 'draft' | 'published' | 'archived';
  template: string;
  title_en: string;
  title_fr: string;
  show_hero: boolean;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  published_at: string | null;
}

// ── Helpers ──

export function generateBlockId(): string {
  return crypto.randomUUID();
}

export function createBlock(type: BlockType): ContentBlock {
  const id = generateBlockId();
  switch (type) {
    case 'heading':
      return { type, id, level: 2, text: '' };
    case 'paragraph':
      return { type, id, text: '' };
    case 'list':
      return { type, id, style: 'bullet', items: [''] };
    case 'callout':
      return { type, id, variant: 'info', text: '' };
    case 'divider':
      return { type, id };
    case 'quote':
      return { type, id, text: '' };
    case 'spacer':
      return { type, id, size: 'md' };
    case 'image':
      return { type, id, url: '', alt: '' };
    case 'table':
      return { type, id, headers: ['Column 1', 'Column 2'], rows: [['', '']] };
  }
}

// Reserved slugs that conflict with existing app routes
export const RESERVED_SLUGS = [
  'login',
  'register',
  'forgot-password',
  'reset-password',
  'dashboard',
  'settings',
  'transactions',
  'accounts',
  'goals',
  'budgets',
  'ai-assistant',
  'calendar',
  'admin',
];

export function isSlugReserved(slug: string): boolean {
  const first = slug.split('/')[0];
  return RESERVED_SLUGS.includes(first);
}

export function isSlugValid(slug: string): boolean {
  return /^[a-z0-9]+(?:[-/][a-z0-9]+)*$/.test(slug);
}
